// iOS-style Match Generation Service
// Replicates the sophisticated algorithm from the iOS app
// Created by Assistant to match iOS implementation

import { 
  Player, 
  Court, 
  Match, 
  GenerationMode, 
  MatchType, 
  MatchConfiguration, 
  PlayerMatchStats, 
  RoundResult 
} from '../models/types';

// Helper function to rotate array (like iOS Array extension)
function rotateArray<T>(array: T[], positions: number): T[] {
  const n = array.length;
  if (n === 0) return array;
  const k = positions % n;
  if (k === 0) return array;
  return [...array.slice(k), ...array.slice(0, k)];
}

export class IOSMatchGenerationService {
  private playerMatchStats: Map<string, PlayerMatchStats> = new Map();

  constructor() {
    console.log('🎾 iOS-style Match Generation Service initialized');
  }

  // Public API matching iOS protocol
  generate(
    players: Player[],
    courts: Court[],
    roundNumber: number,
    isDoublesOnly: boolean = false,
    generationMode: GenerationMode = GenerationMode.BALANCED
  ): RoundResult {
    if (players.length < 2) {
      return {
        roundNumber,
        matches: [],
        unmatchedPlayers: players,
        matchConfiguration: {
          singlesPerRound: 0,
          doublesPerRound: 0,
          isValid: true,
          totalPlayerSlots: 0
        }
      };
    }

    const matchConfiguration = this.calculateOptimalMatchConfiguration(
      players.length,
      courts.length,
      isDoublesOnly,
      generationMode
    );

    return this.generateFairRoundRobinRound(
      roundNumber,
      players,
      courts,
      matchConfiguration
    );
  }

  getPlayerMatchStats(): Map<string, PlayerMatchStats> {
    return new Map(this.playerMatchStats);
  }

  resetPlayerStatistics(): void {
    this.playerMatchStats.clear();
  }

  // Core iOS Round-Robin Algorithm
  private generateFairRoundRobinRound(
    roundNumber: number,
    players: Player[],
    courts: Court[],
    matchConfiguration: MatchConfiguration
  ): RoundResult {
    console.log(`👑 Round ${roundNumber}: True Round-Robin Generation`);

    // Sort players deterministically by ID (like iOS)
    let localPlayers = [...players].sort((a, b) => a.id.localeCompare(b.id));

    // Rotate player list for fairness (iOS rotation logic)
    if (roundNumber > 1) {
      const rotationAmount = (roundNumber - 1) % localPlayers.length;
      if (rotationAmount > 0) {
        localPlayers = rotateArray(localPlayers, rotationAmount);
        console.log(`   🔄 Rotated player order by ${rotationAmount} positions.`);
      }
    }

    console.log(`   📋 Player Order: ${localPlayers.map(p => p.name).join(', ')}`);

    const matches: Match[] = [];
    const assignedPlayerIDs = new Set<string>();
    let playerCursor = 0;

    // Alternate assignment order each round (iOS behavior)
    const assignSinglesFirst = roundNumber % 2 !== 0;

    if (assignSinglesFirst) {
      console.log('   ⚖️ Assigning Singles First');
      playerCursor = this.assignSingles(
        localPlayers,
        playerCursor,
        matchConfiguration,
        courts,
        matches,
        assignedPlayerIDs
      );
      playerCursor = this.assignDoubles(
        localPlayers,
        playerCursor,
        matchConfiguration,
        courts,
        roundNumber,
        matches,
        assignedPlayerIDs
      );
    } else {
      console.log('   ⚖️ Assigning Doubles First');
      playerCursor = this.assignDoubles(
        localPlayers,
        playerCursor,
        matchConfiguration,
        courts,
        roundNumber,
        matches,
        assignedPlayerIDs
      );
      playerCursor = this.assignSingles(
        localPlayers,
        playerCursor,
        matchConfiguration,
        courts,
        matches,
        assignedPlayerIDs
      );
    }

    const pausingPlayers = players.filter(p => !assignedPlayerIDs.has(p.id));
    console.log(`   ⏸️ Pausing: ${pausingPlayers.map(p => p.name).join(', ')}`);

    this.updateStatsForRound(players, matches, pausingPlayers, roundNumber);

    return {
      roundNumber,
      matches,
      unmatchedPlayers: pausingPlayers,
      matchConfiguration
    };
  }

  // Singles Assignment (iOS implementation)
  private assignSingles(
    players: Player[],
    cursor: number,
    config: MatchConfiguration,
    courts: Court[],
    matches: Match[],
    assignedPlayerIDs: Set<string>
  ): number {
    let playerCursor = cursor;
    const availableCourts = courts.filter(court => 
      !matches.some(match => match.court.id === court.id)
    );

    for (let i = 0; i < config.singlesPerRound; i++) {
      if (playerCursor + 2 > players.length || i >= availableCourts.length) break;
      
      const court = availableCourts[i];
      const matchPlayers = players.slice(playerCursor, playerCursor + 2);
      
      const newMatch: Match = {
        id: this.generateId(),
        players: matchPlayers,
        court,
        isDoubles: false
      };
      
      matches.push(newMatch);
      matchPlayers.forEach(player => assignedPlayerIDs.add(player.id));
      playerCursor += 2;
      
      console.log(`   🎾 Singles: ${matchPlayers[0].name} vs ${matchPlayers[1].name} on Court ${court.label}`);
    }
    
    return playerCursor;
  }

  // Doubles Assignment with Partnership Cost Calculation (iOS implementation)
  private assignDoubles(
    players: Player[],
    cursor: number,
    config: MatchConfiguration,
    courts: Court[],
    roundNumber: number,
    matches: Match[],
    assignedPlayerIDs: Set<string>
  ): number {
    let playerCursor = cursor;
    const availableCourts = courts.filter(court => 
      !matches.some(match => match.court.id === court.id)
    );

    for (let i = 0; i < config.doublesPerRound; i++) {
      if (playerCursor + 4 > players.length || i >= availableCourts.length) break;
      
      const court = availableCourts[i];
      const quartet = players.slice(playerCursor, playerCursor + 4);
      const [p1, p2, p3, p4] = quartet;

      // Define the 3 possible unique pairings for 4 players (iOS logic)
      const pairingA = { team1: [p1, p2], team2: [p3, p4] };
      const pairingB = { team1: [p1, p3], team2: [p2, p4] };
      const pairingC = { team1: [p1, p4], team2: [p2, p3] };

      // Helper to get historical partnership count
      const getPartnershipCount = (playerA: Player, playerB: Player): number => {
        const stats = this.playerMatchStats.get(playerA.id);
        return stats?.partnerHistory.get(playerB.id) ?? 0;
      };

      // Calculate "cost" for each pairing based on history (iOS logic)
      const costA = getPartnershipCount(p1, p2) + getPartnershipCount(p3, p4);
      const costB = getPartnershipCount(p1, p3) + getPartnershipCount(p2, p4);
      const costC = getPartnershipCount(p1, p4) + getPartnershipCount(p2, p3);

      // Find the pairing with minimum cost (iOS logic)
      const costedPairings = [
        { pairing: pairingA, cost: costA },
        { pairing: pairingB, cost: costB },
        { pairing: pairingC, cost: costC }
      ];

      const bestPairing = costedPairings.sort((a, b) => a.cost - b.cost)[0].pairing;
      const teamPlayers = [...bestPairing.team1, ...bestPairing.team2];

      const newMatch: Match = {
        id: this.generateId(),
        players: teamPlayers,
        court,
        isDoubles: true
      };

      matches.push(newMatch);
      teamPlayers.forEach(player => assignedPlayerIDs.add(player.id));
      playerCursor += 4;

      console.log(`   🎾 Doubles: ${teamPlayers[0].name}+${teamPlayers[1].name} vs ${teamPlayers[2].name}+${teamPlayers[3].name} on Court ${court.label}`);
    }

    return playerCursor;
  }

  // Statistics Update (iOS implementation)
  private updateStatsForRound(
    players: Player[],
    matches: Match[],
    pausingPlayers: Player[],
    roundNumber: number
  ): void {
    const playingPlayerIDs = new Set(matches.flatMap(match => match.players.map(p => p.id)));

    // Initialize stats for new players
    players.forEach(player => {
      if (!this.playerMatchStats.has(player.id)) {
        this.playerMatchStats.set(player.id, {
          singlesCount: 0,
          doublesCount: 0,
          pauseCount: 0,
          partnerHistory: new Map(),
          opponentHistory: new Map(),
          lastMatchType: MatchType.NONE,
          lastRoundMatchType: MatchType.NONE,
          roundHistory: new Map(),
          pauseHistory: new Map(),
          totalMatches: 0
        });
      }
    });

    // Update match counts and round history
    players.forEach(player => {
      const stats = this.playerMatchStats.get(player.id)!;
      
      if (playingPlayerIDs.has(player.id)) {
        const match = matches.find(m => m.players.some(p => p.id === player.id))!;
        if (match.isDoubles) {
          stats.doublesCount++;
          stats.lastMatchType = MatchType.DOUBLES;
          stats.roundHistory.set(roundNumber, MatchType.DOUBLES);
        } else {
          stats.singlesCount++;
          stats.lastMatchType = MatchType.SINGLES;
          stats.roundHistory.set(roundNumber, MatchType.SINGLES);
        }
        stats.pauseHistory.set(roundNumber, false);
      } else {
        stats.pauseCount++;
        stats.lastMatchType = MatchType.PAUSE;
        stats.roundHistory.set(roundNumber, MatchType.PAUSE);
        stats.pauseHistory.set(roundNumber, true);
      }
      
      stats.totalMatches = stats.singlesCount + stats.doublesCount;
    });

    // Update partnership histories for doubles matches
    matches.forEach(match => {
      if (match.isDoubles) {
        const players = match.players;
        // Team 1 partners
        const team1Stats1 = this.playerMatchStats.get(players[0].id)!;
        const team1Stats2 = this.playerMatchStats.get(players[1].id)!;
        team1Stats1.partnerHistory.set(players[1].id, (team1Stats1.partnerHistory.get(players[1].id) ?? 0) + 1);
        team1Stats2.partnerHistory.set(players[0].id, (team1Stats2.partnerHistory.get(players[0].id) ?? 0) + 1);
        
        // Team 2 partners
        const team2Stats1 = this.playerMatchStats.get(players[2].id)!;
        const team2Stats2 = this.playerMatchStats.get(players[3].id)!;
        team2Stats1.partnerHistory.set(players[3].id, (team2Stats1.partnerHistory.get(players[3].id) ?? 0) + 1);
        team2Stats2.partnerHistory.set(players[2].id, (team2Stats2.partnerHistory.get(players[2].id) ?? 0) + 1);
      }
    });
  }

  // Match Configuration Calculation (iOS implementation)
  private calculateOptimalMatchConfiguration(
    totalPlayers: number,
    courts: number,
    isDoublesOnly: boolean,
    generationMode: GenerationMode = GenerationMode.BALANCED
  ): MatchConfiguration {
    if (totalPlayers < 2) {
      return {
        singlesPerRound: 0,
        doublesPerRound: 0,
        isValid: true,
        totalPlayerSlots: 0
      };
    }

    if (isDoublesOnly) {
      const maxDoubles = Math.min(courts, Math.floor(totalPlayers / 4));
      return {
        singlesPerRound: 0,
        doublesPerRound: maxDoubles,
        isValid: true,
        totalPlayerSlots: maxDoubles * 4
      };
    }

    // Find best configuration of singles and doubles (iOS logic)
    let bestConfig = { singlesPerRound: 0, doublesPerRound: 0 };
    let minUnusedPlayers = totalPlayers;

    for (let d = Math.min(courts, Math.floor(totalPlayers / 4)); d >= 0; d--) {
      const remainingPlayers = totalPlayers - d * 4;
      const remainingCourts = courts - d;
      const s = Math.min(remainingCourts, Math.floor(remainingPlayers / 2));
      const unusedPlayers = totalPlayers - (d * 4 + s * 2);

      if (unusedPlayers < minUnusedPlayers) {
        minUnusedPlayers = unusedPlayers;
        bestConfig = { singlesPerRound: s, doublesPerRound: d };
      }
    }

    return {
      singlesPerRound: bestConfig.singlesPerRound,
      doublesPerRound: bestConfig.doublesPerRound,
      isValid: true,
      totalPlayerSlots: (bestConfig.singlesPerRound * 2) + (bestConfig.doublesPerRound * 4)
    };
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
} 